<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <page-header
      title="Opakowania"
      buttonLabel="Dodaj opakowanie"
      buttonLink="/packages/new" />

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <form
        style="width: 100%; display: flex; gap: 0.5rem; align-items: center;">
        <div style="flex: 1;">
          <d-input
            placeholder="Podaj nazwę opakowania"
            v-model="filters.search" />
        </div>

        <div style="width: 360px; display: flex; align-items: center;">
          <label style="margin: 0; display: flex; align-items: center; cursor: pointer;">
            <input
              type="checkbox"
              v-model="filters.is_drink"
              style="margin-right: 0.5rem;">
            <span>Pokazuj tylko opakowania napojów</span>
          </label>
        </div>

        <d-button
          theme="success"
          outline
          class="text-nowrap"
          @click.prevent="searchWithFilters">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </form>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="thead-dark">
          <tr>
            <sortable-table-heading field="name" view="packages" :column="orderColumn" :direction="orderDirection">Opakowanie</sortable-table-heading>
            <th scope="col" class="border-0">Typ opakowania</th>
            <th scope="col" class="border-0 text-center" style="width: 1%; white-space: nowrap;">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(pkg, index) of packages"
            :key="'package-' + index">
            <td class="align-middle">
              <router-link :to="'/packages/' + pkg.id">
                {{ pkg.name }}
              </router-link>
            </td>
            <td
              class="align-middle"
              style="width: 250px;">
              <small v-if="pkg.is_drink" class="text-secondary">Opakowanie dla napoju</small>
              <small v-if="!pkg.is_drink">Opakowanie dla posiłku</small>
            </td>
            <td
              class="align-middle text-right"
              style="width: 1%; white-space: nowrap;">
              <div style="display: flex; justify-content: flex-end; gap: 0.5rem;">
                <d-button
                  theme="warning"
                  @click.prevent="showDuplicateModal(pkg)"
                  title="Duplikuj">
                  <i class="material-icons" style="color:white">content_copy</i>
                </d-button>
                <d-button
                  theme="dark"
                  @click.native="$router.push('/packages/' + pkg.id)"
                  title="Edytuj">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  :disabled="removeInProgress"
                  @click.native="removePackage(pkg.id)"
                  title="Usuń">
                  <i class="material-icons">delete</i>
                </d-button>
              </div>
            </td>
          </tr>
          <tr v-if="packages.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak opakowań do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'packages',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      packages: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        is_drink: false
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    showDuplicateModal(pkg) {
      this.$swal({
        title: 'Potwierdzenie duplikacji',
        html: 'Czy na pewno chcesz zduplikować opakowanie - <b>' + (pkg.name || '') + '</b>?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Duplikuj',
        cancelButtonText: 'Anuluj',
        buttonsStyling: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.duplicatePackage(pkg.id);
        }
      });
    },
    duplicatePackage(packageId) {
      this.$http
        .post('/packages/duplicate/' + packageId)
        .then((response) => {
          if (response.data.success) {
            this.$swal({
              title: 'Sukces',
              text: response.data.message || 'Opakowanie zostało zduplikowane',
              icon: 'success'
            }).then(() => {
              this.loadFilteredData();
            });
          } else {
            this.$swal({
              title: 'Błąd',
              text: response.data.message || 'Nie udało się zduplikować opakowania',
              icon: 'error'
            });
          }
        })
        .catch((error) => {
          this.$swal({
            title: 'Błąd',
            text: 'Wystąpił problem podczas duplikowania opakowania',
            icon: 'error'
          });
          console.error(error);
        });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      if (this.filters.is_drink) {
        where.is_drink = 1;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/packages/items?page=' + this.currentPage,
        listField: 'packages',
        pagination: true,
        params: {
          where,
          order: {
            column: this.orderColumn,
            direction: this.orderDirection
          }
        }
      });
    },
    removePackage (packageID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/packages/delete/',
        id: packageID,
        successTitle: 'Usunięto opakowanie',
        successText: 'Wybrane opakowanie zostało usunięte',
        confirmTitle: 'Czy na pewno chcesz usunąć to opakowanie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie opakowania nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.emit('view-filters-reset');
    },
    searchWithFilters () {
      this.$bus.emit('pagination-reset');
      this.debouncedLoadFilteredData();
    }
  }
};
</script>
